import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import PrimaryTabNavigation from 'components/ui/PrimaryTabNavigation';
import GameCanvas from './components/GameCanvas';
import HUDOverlay from './components/HUDOverlay';
import GameControls from './components/GameControls';
import GameMetaPanel from './components/GameMetaPanel';
import Leaderboard from './components/Leaderboard';
import KeyboardGuide from './components/KeyboardGuide';

const DEFAULT_GAME = {
  id: "game_001",
  title: "Neon Platformer Adventure",
  prompt: "Create a colorful 2D platformer with a glowing hero who collects coins and defeats slime enemies across floating platforms in a starry night sky",
  creator: "Jane Doe",
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_15ebe7420-1773033730004.png",
  thumbnailAlt: "Colorful neon-lit video game scene with glowing platforms and stars in dark purple night sky",
  genre: "Platformer",
  difficulty: "Medium",
  createdAt: "03/07/2026",
  engine: "HTML5 Canvas",
  plays: 1247,
  rating: 4.7
};

export default function GamePlayerScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const gameData = location?.state?.game || DEFAULT_GAME;

  const [isPaused, setIsPaused] = useState(false);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [level] = useState(1);
  const [timeLeft, setTimeLeft] = useState(180);
  const [audioOn, setAudioOn] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [achievements, setAchievements] = useState([]);
  const [gameKey, setGameKey] = useState(0);
  const [isGameOver, setIsGameOver] = useState(false);
  const [isWon, setIsWon] = useState(false);
  const [highScore, setHighScore] = useState(() => {
    try { return parseInt(localStorage.getItem(`hs_${gameData?.id}`) || '0'); } catch { return 0; }
  });
  const [shareToast, setShareToast] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const timerRef = useRef(null);
  const prevScoreRef = useRef(0);
  const prevLivesRef = useRef(3);

  // Timer
  useEffect(() => {
    if (!isPaused && !isGameOver && !isWon && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft((t) => {
          if (t <= 1) { clearInterval(timerRef?.current); return 0; }
          return t - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerRef?.current);
    }
    return () => clearInterval(timerRef?.current);
  }, [isPaused, isGameOver, isWon, timeLeft]);

  // Time out = game over
  useEffect(() => {
    if (timeLeft === 0 && !isGameOver && !isWon) {
      setIsGameOver(true);
    }
  }, [timeLeft, isGameOver, isWon]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (e?.code === 'KeyP') setIsPaused((p) => !p);
      if (e?.code === 'KeyR') handleRestart();
      if (e?.code === 'Escape') setIsPaused((p) => !p);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const handleScoreUpdate = useCallback((newScore) => {
    setScore(newScore);
    if (newScore >= 200 && prevScoreRef?.current < 200) {
      setAchievements((a) => [...a, '🏆 First Kill!']);
    }
    if (newScore >= 500 && prevScoreRef?.current < 500) {
      setAchievements((a) => [...a, '⭐ High Scorer!']);
    }
    if (newScore >= 1000 && prevScoreRef?.current < 1000) {
      setAchievements((a) => [...a, '🔥 On Fire!']);
    }
    prevScoreRef.current = newScore;
  }, []);

  const handleLivesUpdate = useCallback((newLives) => {
    setLives(newLives);
    prevLivesRef.current = newLives;
  }, []);

  const handleGameOver = useCallback((finalScore) => {
    setIsGameOver(true);
    setScore(finalScore);
    // Save high score
    if (finalScore > highScore) {
      setHighScore(finalScore);
      try { localStorage.setItem(`hs_${gameData?.id}`, String(finalScore)); } catch {}
    }
  }, [highScore, gameData?.id]);

  const handleWin = useCallback((finalScore) => {
    setIsWon(true);
    setScore(finalScore);
    setAchievements((a) => [...a, '🎉 You Won!']);
    if (finalScore > highScore) {
      setHighScore(finalScore);
      try { localStorage.setItem(`hs_${gameData?.id}`, String(finalScore)); } catch {}
    }
  }, [highScore, gameData?.id]);

  const handleRestart = () => {
    setGameKey((k) => k + 1);
    setScore(0);
    setLives(3);
    setTimeLeft(180);
    setIsGameOver(false);
    setIsWon(false);
    setAchievements([]);
    prevScoreRef.current = 0;
    prevLivesRef.current = 3;
    setIsPaused(false);
  };

  const handleShare = async () => {
    const shareUrl = `${window.location?.origin}/game-player-screen?id=${gameData?.id}`;
    const shareText = `Playing "${gameData?.title}" on GameForge! My score: ${score?.toLocaleString()}`;
    if (navigator?.share) {
      try {
        await navigator.share({ title: gameData?.title, text: shareText, url: shareUrl });
        return;
      } catch {}
    }
    try {
      await navigator.clipboard?.writeText(`${shareText} ${shareUrl}`);
      setShareToast(true);
      setTimeout(() => setShareToast(false), 2500);
    } catch {}
  };

  const handleSaveProgress = () => {
    try {
      const progress = { gameId: gameData?.id, score, lives, timeLeft, achievements, savedAt: new Date()?.toISOString() };
      localStorage.setItem(`progress_${gameData?.id}`, JSON.stringify(progress));
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 2500);
    } catch {}
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryTabNavigation />
      <main className="main-content">
        {/* Top bar */}
        <div className="sticky top-16 z-40 border-b" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-2 flex items-center gap-3 flex-wrap">
            <Button
              variant="ghost"
              size="sm"
              iconName="ArrowLeft"
              iconPosition="left"
              iconSize={16}
              onClick={() => navigate('/home-landing-screen')}
            >
              <span className="hidden sm:inline">Back to Library</span>
            </Button>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading font-bold text-sm md:text-base truncate" style={{ color: 'var(--color-foreground)' }}>
                {gameData?.title}
              </h1>
            </div>

            <div className="flex items-center gap-2">
              {highScore > 0 && (
                <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', color: 'var(--color-warning)' }}>
                  <Icon name="Trophy" size={13} color="var(--color-warning)" />
                  <span style={{ fontFamily: 'var(--font-data)' }}>Best: {highScore?.toLocaleString()}</span>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                iconName="Share2"
                iconPosition="left"
                iconSize={14}
                onClick={handleShare}
              >
                <span className="hidden sm:inline">{shareToast ? 'Copied!' : 'Share'}</span>
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Wand2"
                iconPosition="left"
                iconSize={14}
                onClick={() => navigate('/creator-dashboard')}
              >
                <span className="hidden sm:inline">Create Similar</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-6">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">

            {/* Main game area */}
            <div className="flex-1 min-w-0">
              {/* HUD */}
              <div className="mb-2 rounded-xl px-2" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                <HUDOverlay
                  score={score}
                  lives={lives}
                  level={level}
                  timeLeft={timeLeft}
                  achievements={achievements}
                />
              </div>

              {/* Canvas */}
              <div className="rounded-xl overflow-hidden relative" style={{ border: '2px solid rgba(99,102,241,0.4)', boxShadow: '0 0 32px rgba(99,102,241,0.2)' }}>
                {isPaused && !isGameOver && !isWon && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl" style={{ background: 'rgba(0,0,0,0.7)' }}>
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-3" style={{ background: 'rgba(99,102,241,0.3)', border: '2px solid rgba(99,102,241,0.6)' }}>
                      <Icon name="Pause" size={32} color="white" />
                    </div>
                    <p className="font-heading font-bold text-xl text-white mb-1">Paused</p>
                    <p className="font-caption text-sm mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>Press P or click Resume to continue</p>
                    <Button variant="default" size="sm" onClick={() => setIsPaused(false)}>Resume Game</Button>
                  </div>
                )}
                {(isGameOver || isWon) && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl" style={{ background: 'rgba(0,0,0,0.8)' }}>
                    <div
                      className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
                      style={{ background: isWon ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)', border: `2px solid ${isWon ? 'rgba(16,185,129,0.6)' : 'rgba(239,68,68,0.6)'}` }}
                    >
                      <Icon name={isWon ? 'Trophy' : 'Skull'} size={40} color={isWon ? '#10b981' : '#ef4444'} />
                    </div>
                    <p className="font-heading font-bold text-2xl text-white mb-1">{isWon ? '🎉 You Win!' : '💀 Game Over'}</p>
                    <p className="font-data text-3xl font-bold mb-1" style={{ color: isWon ? '#10b981' : '#ef4444' }}>{score?.toLocaleString()}</p>
                    <p className="font-caption text-sm mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>Final Score</p>
                    {score > highScore && score > 0 && (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-4" style={{ background: 'rgba(245,158,11,0.2)', border: '1px solid rgba(245,158,11,0.5)' }}>
                        <Icon name="Star" size={14} color="#f59e0b" />
                        <span className="text-xs font-semibold" style={{ color: '#f59e0b' }}>New High Score!</span>
                      </div>
                    )}
                    <div className="flex gap-3 mt-2">
                      <Button variant="default" size="sm" iconName="RotateCcw" iconPosition="left" onClick={handleRestart}>Play Again</Button>
                      <Button variant="outline" size="sm" iconName="Library" iconPosition="left" onClick={() => navigate('/home-landing-screen')}>More Games</Button>
                    </div>
                  </div>
                )}
                <GameCanvas
                  key={gameKey}
                  gameData={gameData}
                  isPaused={isPaused}
                  onScoreUpdate={handleScoreUpdate}
                  onLivesUpdate={handleLivesUpdate}
                  onGameOver={handleGameOver}
                  onWin={handleWin}
                />
              </div>

              {/* Controls bar */}
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                <GameControls
                  isPaused={isPaused}
                  onPause={() => setIsPaused((p) => !p)}
                  onRestart={handleRestart}
                  onToggleAudio={() => setAudioOn((a) => !a)}
                  audioOn={audioOn}
                  onToggleInfo={() => setShowInfo((s) => !s)}
                />
                <div className="flex items-center gap-2">
                  <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                    {isWon ? '🎉 You collected all coins!' : isGameOver ? '💀 Game over — restart to try again' : 'Collect all coins to win!'}
                  </span>
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: isGameOver ? 'var(--color-error)' : isWon ? 'var(--color-accent)' : 'var(--color-success)' }} />
                </div>
              </div>

              {/* Info panel */}
              {showInfo && (
                <div className="mt-3">
                  <GameMetaPanel game={gameData} isOpen={showInfo} onClose={() => setShowInfo(false)} />
                </div>
              )}

              {/* Keyboard guide */}
              <div className="mt-3 hidden md:block">
                <KeyboardGuide />
              </div>
            </div>

            {/* Sidebar */}
            <div className="w-full lg:w-72 xl:w-80 flex-shrink-0 space-y-4">
              {/* Score card */}
              <div className="rounded-xl p-4 text-center" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.15))', border: '1px solid rgba(99,102,241,0.3)' }}>
                <p className="font-caption text-xs font-semibold mb-1" style={{ color: 'var(--color-muted-foreground)' }}>CURRENT SCORE</p>
                <p className="font-data text-4xl font-bold" style={{ color: 'var(--color-primary)' }}>
                  {score?.toLocaleString()}
                </p>
                {highScore > 0 && (
                  <p className="font-caption text-xs mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
                    Best: <span style={{ color: 'var(--color-warning)', fontFamily: 'var(--font-data)' }}>{highScore?.toLocaleString()}</span>
                  </p>
                )}
                <div className="flex items-center justify-center gap-4 mt-3">
                  <div className="text-center">
                    <p className="font-data text-lg font-semibold" style={{ color: 'var(--color-foreground)' }}>{level}</p>
                    <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Level</p>
                  </div>
                  <div className="w-px h-8" style={{ background: 'var(--color-border)' }} />
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      {Array.from({ length: 3 })?.map((_, i) => (
                        <Icon key={i} name="Heart" size={16} color={i < lives ? '#ef4444' : 'rgba(239,68,68,0.2)'} />
                      ))}
                    </div>
                    <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Lives</p>
                  </div>
                  <div className="w-px h-8" style={{ background: 'var(--color-border)' }} />
                  <div className="text-center">
                    <p className="font-data text-lg font-semibold" style={{ color: timeLeft < 30 ? 'var(--color-error)' : 'var(--color-foreground)' }}>
                      {String(Math.floor(timeLeft / 60))?.padStart(2, '0')}:{String(timeLeft % 60)?.padStart(2, '0')}
                    </p>
                    <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Time</p>
                  </div>
                </div>
              </div>

              {/* Leaderboard */}
              <Leaderboard currentScore={score} gameId={gameData?.id} />

              {/* Quick actions */}
              <div className="rounded-xl p-4 space-y-2" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                <p className="font-caption text-xs font-semibold mb-3" style={{ color: 'var(--color-muted-foreground)' }}>QUICK ACTIONS</p>
                <Button variant="outline" size="sm" fullWidth iconName="Library" iconPosition="left" iconSize={16} onClick={() => navigate('/home-landing-screen')}>
                  Game Library
                </Button>
                <Button variant="outline" size="sm" fullWidth iconName="Wand2" iconPosition="left" iconSize={16} onClick={() => navigate('/creator-dashboard')}>
                  Creator Dashboard
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  fullWidth
                  iconName={savedToast ? 'CheckCircle2' : 'BookmarkPlus'}
                  iconPosition="left"
                  iconSize={16}
                  onClick={handleSaveProgress}
                >
                  {savedToast ? 'Progress Saved!' : 'Save Progress'}
                </Button>
              </div>

              {/* Mobile keyboard guide */}
              <div className="md:hidden">
                <KeyboardGuide />
              </div>

              {/* Achievements */}
              {achievements?.length > 0 && (
                <div className="rounded-xl p-4" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Award" size={16} color="var(--color-accent)" />
                    <p className="font-caption text-xs font-semibold" style={{ color: 'var(--color-foreground)' }}>ACHIEVEMENTS</p>
                  </div>
                  <div className="space-y-2">
                    {achievements?.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
                        <span className="font-body text-sm" style={{ color: 'var(--color-foreground)' }}>{a}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}